import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../PlaceholderTag";

/** Physical mending frame stays visible independently of the Aura pattern. */
export class GarmentStationVisuals {
  readonly ready: Promise<void>;
  private readonly root: TransformNode;
  private disposed = false;
  private model: InstantiatedModel | null = null;
  private patch: Mesh | null = null;

  constructor(private readonly scene: Scene, position: Vector3, onLoaded: () => void) {
    this.root = new TransformNode("garmentStation", scene);
    this.root.position.copyFrom(position);
    this.ready = this.load(onLoaded);
  }

  private async load(onLoaded: () => void): Promise<void> {
    try {
      const model = await getAssetManager().instantiateModel(this.scene,
        "/assets/models/environment/garment_station.glb", undefined, { doNotInstantiate: true });
      if (this.disposed || this.scene.isDisposed) { model.dispose(); return; }
      const patch = model.meshes.find((m) => m.name.endsWith("GarmentPatch_template"));
      if (!(patch instanceof Mesh) || !patch.geometry || !(patch.material instanceof PBRMaterial)) {
        model.dispose();
        throw new Error("Garment station is missing its textured cloth prefab");
      }
      for (const root of model.rootNodes) { root.parent = this.root; tagAs(root, "final"); }
      for (const mesh of model.meshes) {
        mesh.isPickable = false; mesh.receiveShadows = true; tagAs(mesh, "final");
      }
      patch.setEnabled(false);
      this.model = model; this.patch = patch;
      onLoaded();
    } catch (error) {
      if (!this.disposed) console.warn("[GarmentStationVisuals] Keeping puzzle fallback:", error);
    }
  }

  createPatch(name: string, color: Color3, alpha: number): Mesh | null {
    if (!this.patch?.geometry || this.disposed) return null;
    const mesh = new Mesh(name, this.scene);
    this.patch.geometry.applyToMesh(mesh);
    const material = new PBRMaterial(name + "_linen", this.scene);
    // Borrow the station's texture; individual patches never own/dispose it.
    material.albedoTexture = (this.patch.material as PBRMaterial).albedoTexture;
    material.albedoColor = color;
    material.roughness = 1; material.metallic = 0; material.alpha = alpha;
    material.backFaceCulling = false; material.twoSidedLighting = true;
    if (alpha < 1) material.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHABLEND;
    mesh.material = material; mesh.isPickable = false;
    tagAs(mesh, "final");
    return mesh;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.patch = null;
    this.model?.dispose(); this.model = null;
    this.root.dispose();
  }
}
